import {
  Activity,
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  Loader2,
  Play,
  RefreshCw,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/use-toast";
import { useAuth } from "../contexts/AuthContext";

const ADMIN_EMAILS = ["lu@wavegen.ai", "lulu.li1989@gmail.com"];

interface ProviderSummary {
  eligible?: number;
  visibility_rate?: number;
  recommendation_rate?: number;
  citation_rate?: number;
}

interface ReportItem {
  batchId: string;
  cohort: string;
  status: string;
  startedAt: string;
  completedAt?: string | null;
  promptVersion?: string;
  responseCount?: number;
  errorCount?: number;
  discoveryProviders?: Record<string, ProviderSummary>;
}

interface ReportManifest {
  generatedAt: string;
  latestBatchId: string | null;
  latestStatus?: string | null;
  schedule?: { label?: string; timezone?: string };
  reports: ReportItem[];
  lastError?: string | null;
  source?: "cloud" | "local";
}

const API_BASE = process.env.REACT_APP_API_BASE || "";

function percent(value?: number): string {
  return typeof value === "number" ? `${(value * 100).toFixed(1)}%` : "—";
}

function formatRunDate(value?: string | null): string {
  if (!value) return "In progress";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function providerLabel(provider: string): string {
  if (provider === "openai") return "OpenAI";
  if (provider === "perplexity") return "Perplexity";
  if (provider === "gemini") return "Gemini";
  return provider;
}

export default function AeoVisibilityPage() {
  const { currentUser, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [manifest, setManifest] = useState<ReportManifest | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [reportHtml, setReportHtml] = useState("");
  const [loadingManifest, setLoadingManifest] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [startingRun, setStartingRun] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authenticatedFetch = useCallback(
    async (path: string, init?: RequestInit) => {
      if (!currentUser) throw new Error("Sign in is required");
      const token = await currentUser.getIdToken();
      return fetch(`${API_BASE}${path}`, {
        ...init,
        headers: {
          ...init?.headers,
          Authorization: `Bearer ${token}`,
        },
      });
    },
    [currentUser],
  );

  const loadManifest = useCallback(async () => {
    if (!currentUser) return;
    setLoadingManifest(true);
    setError(null);
    try {
      const response = await authenticatedFetch("/api/admin/aeo/reports");
      if (!response.ok) throw new Error("Could not load the AEO report index");
      const nextManifest = (await response.json()) as ReportManifest;
      setManifest(nextManifest);
      setSelectedBatchId((current) =>
        current &&
        nextManifest.reports.some((report) => report.batchId === current)
          ? current
          : nextManifest.latestBatchId ||
            nextManifest.reports[0]?.batchId ||
            "",
      );
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Could not load reports",
      );
    } finally {
      setLoadingManifest(false);
    }
  }, [authenticatedFetch, currentUser]);

  const loadReport = useCallback(
    async (batchId: string) => {
      if (!batchId) {
        setReportHtml("");
        return;
      }
      setLoadingReport(true);
      setError(null);
      try {
        const response = await authenticatedFetch(
          `/api/admin/aeo/reports/${encodeURIComponent(batchId)}`,
        );
        if (!response.ok) throw new Error("Could not load this report");
        setReportHtml(await response.text());
      } catch (loadError) {
        setReportHtml("");
        setError(
          loadError instanceof Error
            ? loadError.message
            : "Could not load report",
        );
      } finally {
        setLoadingReport(false);
      }
    },
    [authenticatedFetch],
  );

  useEffect(() => {
    if (currentUser && !authLoading) void loadManifest();
  }, [authLoading, currentUser, loadManifest]);

  useEffect(() => {
    if (selectedBatchId) void loadReport(selectedBatchId);
  }, [loadReport, selectedBatchId]);

  const selectedReport = useMemo(
    () =>
      manifest?.reports.find((report) => report.batchId === selectedBatchId),
    [manifest, selectedBatchId],
  );

  const startRun = async () => {
    const confirmed = window.confirm(
      "Start the full 50-prompt weekly benchmark? It makes 351 provider calls and is currently estimated at about $13.37.",
    );
    if (!confirmed) return;
    setStartingRun(true);
    try {
      const response = await authenticatedFetch("/api/admin/aeo/run", {
        method: "POST",
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || "Could not start the cloud run");
      }
      toast({
        title: "AEO monitor started",
        description:
          "It will email you and update this page when the run finishes.",
      });
    } catch (runError) {
      toast({
        title: "Could not start the monitor",
        description:
          runError instanceof Error ? runError.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setStartingRun(false);
    }
  };

  if (!authLoading && currentUser) {
    const isAdmin = ADMIN_EMAILS.includes(currentUser.email || "");
    if (!isAdmin) return <Navigate to="/generate" replace />;
  }

  if (authLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Checking access…
      </div>
    );
  }

  return (
    <main className="mx-auto flex w-full max-w-[1500px] flex-col gap-6 px-4 py-6 md:px-8">
      <header className="flex flex-col gap-5 border-b border-border pb-6 xl:flex-row xl:items-end xl:justify-between">
        <div className="flex flex-col gap-3">
          <Link
            to="/admin"
            className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Admin dashboard
          </Link>
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-brand-ink">
              <Activity className="h-4 w-4" />
              Answer-engine visibility
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              What AI recommends when buyers ask
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
              A fixed weekly benchmark across OpenAI, Gemini, and
              Perplexity—with every answer, citation, and competitor mapping
              available below.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void loadManifest()}
            disabled={loadingManifest}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${loadingManifest ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => void startRun()}
            disabled={startingRun}
          >
            {startingRun ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Run benchmark
          </Button>
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-3">
          {(
            Object.entries(selectedReport?.discoveryProviders || {}) as Array<
              [string, ProviderSummary]
            >
          ).map(([provider, values]) => (
            <div key={provider} className="bg-background px-5 py-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <span className="text-sm font-semibold">
                  {providerLabel(provider)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {values.eligible ?? 0} answers
                </span>
              </div>
              <dl className="grid grid-cols-3 gap-3">
                <div>
                  <dt className="text-[11px] text-muted-foreground">Visible</dt>
                  <dd className="mt-1 text-lg font-semibold tabular-nums">
                    {percent(values.visibility_rate)}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] text-muted-foreground">
                    Recommended
                  </dt>
                  <dd className="mt-1 text-lg font-semibold tabular-nums">
                    {percent(values.recommendation_rate)}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] text-muted-foreground">Cited</dt>
                  <dd className="mt-1 text-lg font-semibold tabular-nums">
                    {percent(values.citation_rate)}
                  </dd>
                </div>
              </dl>
            </div>
          ))}
          {!selectedReport?.discoveryProviders ||
          Object.keys(selectedReport.discoveryProviders).length === 0 ? (
            <div className="col-span-full bg-background px-5 py-8 text-sm text-muted-foreground">
              Scorecard data will appear after the first cloud report is
              published.
            </div>
          ) : null}
        </div>

        <div className="flex flex-col justify-between gap-4 rounded-lg bg-[#201d1b] px-5 py-4 text-[#fffaf7]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#c7bbb4]">
                Automation
              </div>
              <div className="mt-1 text-sm font-medium">
                {manifest?.schedule?.label || "Weekly schedule"}
              </div>
            </div>
            <CalendarClock className="h-5 w-5 text-[#ff8a6f]" />
          </div>
          <div className="flex items-center gap-2 text-xs text-[#c7bbb4]">
            {manifest?.latestStatus === "completed" ? (
              <CheckCircle2 className="h-4 w-4 text-[#7fc89a]" />
            ) : (
              <Activity className="h-4 w-4 text-[#ff8a6f]" />
            )}
            {manifest?.source === "local"
              ? "Local report preview"
              : `Latest: ${manifest?.latestStatus || "waiting for first run"}`}
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-lg border border-border bg-background">
        <div className="flex flex-col gap-4 border-b border-border px-4 py-4 md:flex-row md:items-center md:justify-between md:px-5">
          <div>
            <div className="text-sm font-semibold">Prompt-level report</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {selectedReport
                ? `${formatRunDate(selectedReport.completedAt)} · ${selectedReport.responseCount ?? "—"} responses · ${selectedReport.errorCount ?? 0} errors`
                : "Choose a completed run"}
            </div>
          </div>
          <label className="relative block min-w-0 md:w-[28rem]">
            <span className="sr-only">Choose report run</span>
            <select
              value={selectedBatchId}
              onChange={(event) => setSelectedBatchId(event.target.value)}
              disabled={!manifest?.reports.length}
              className="h-10 w-full appearance-none rounded-md border border-input bg-background px-3 pr-9 text-sm outline-none transition-colors focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              {!manifest?.reports.length && (
                <option value="">No reports yet</option>
              )}
              {manifest?.reports.map((report) => (
                <option key={report.batchId} value={report.batchId}>
                  {formatRunDate(report.completedAt)} — {report.batchId}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-muted-foreground" />
          </label>
        </div>

        <div className="relative min-h-[720px] bg-[#f7f4f2]">
          {loadingReport ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading every prompt and answer…
            </div>
          ) : null}
          {reportHtml ? (
            <iframe
              key={selectedBatchId}
              title={`AEO visibility report ${selectedBatchId}`}
              srcDoc={reportHtml}
              sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
              className="h-[calc(100vh-10rem)] min-h-[720px] w-full border-0"
            />
          ) : !loadingReport ? (
            <div className="flex min-h-[720px] items-center justify-center px-6 text-center text-sm text-muted-foreground">
              The first report will appear here after the cloud monitor
              finishes.
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}
